// =============================================
// Inventory System Types
// =============================================

// VENDORS
export type Vendor = {
  id: string;
  user_id: string;
  name: string;
  company_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  default_credit_days: number;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type VendorWithOutstanding = Vendor & {
  outstanding_amount: number;
  total_purchases: number;
  total_payments: number;
};

// PRODUCTS
export type Product = {
  id: string;
  user_id: string;
  name: string;
  brand?: string;
  form?: string; // capsule, tablet, powder, liquid
  dosage?: string; // 500mg, 10ml, etc.
  default_selling_price?: number;
  default_currency: string;
  unit_of_measure: string; // units, bottles, packets
  low_stock_threshold: number;
  description?: string;
  instructions?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ProductWithStock = Product & {
  current_stock: number;
  stock_status: 'low' | 'ok' | 'good' | 'out';
};

// INVENTORY PURCHASES
export type InventoryPurchase = {
  id: string;
  user_id: string;
  vendor_id: string;
  product_id: string;
  quantity: number;
  cost_per_unit: number;
  total_cost: number; // generated column
  batch_number?: string;
  expiry_date?: string;
  purchase_date: string;
  invoice_number?: string;
  notes?: string;
  payment_status: 'pending' | 'partial' | 'paid';
  amount_paid: number;
  payment_due_date?: string;
  created_at: string;
  updated_at: string;
};

export type InventoryPurchaseWithDetails = InventoryPurchase & {
  vendor?: Vendor;
  product?: Product;
  remaining_stock?: number;
};

// VENDOR PAYMENTS
export type VendorPayment = {
  id: string;
  user_id: string;
  vendor_id: string;
  purchase_id?: string;
  amount: number;
  currency: string;
  payment_method?: string; // cash, upi, bank_transfer, card, cheque
  payment_date: string;
  transaction_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
};

export type VendorPaymentWithDetails = VendorPayment & {
  vendor?: Vendor;
  purchase?: InventoryPurchase;
};

// CLIENT SUPPLEMENTS
export type ClientSupplement = {
  id: string;
  user_id: string;
  client_id: string;
  product_id: string;
  purchase_id?: string;
  quantity: number;
  selling_price_per_unit: number;
  total_amount: number; // generated column
  assigned_date: string;
  batch_number?: string;
  expiry_date?: string;
  dosage_instructions?: string;
  duration_days?: number;
  follow_up_date?: string;
  payment_id?: string;
  payment_status: 'pending' | 'partial' | 'paid';
  appointment_id?: string; // NEW: links to appointment
  notes?: string;
  created_at: string;
  updated_at: string;
};

export type ClientSupplementWithDetails = ClientSupplement & {
  product?: Product;
  purchase?: InventoryPurchase;
  // Linked entities
  appointment?: {
    id: string;
    title: string;
    starts_at: string;
    status: string;
  };
  payment?: {
    id: string;
    amount: number;
    status: 'pending' | 'paid' | 'expired';
    due_at: string;
  };
};

// PAYMENT LINE ITEMS
export type PaymentLineItemType = 'service' | 'supplement' | 'other';
export type DiscountType = 'percentage' | 'fixed';

export type PaymentLineItem = {
  id: string;
  payment_id: string;
  user_id: string;
  item_type: PaymentLineItemType;
  service_id?: string;
  product_id?: string;
  client_supplement_id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  gross_amount: number; // generated column (quantity * unit_price)
  discount_type?: DiscountType | null;
  discount_value?: number;
  discount_amount?: number;
  discount_reason?: string | null;
  net_amount?: number; // calculated: gross - discount
  created_at: string;
};

export type PaymentLineItemWithDetails = PaymentLineItem & {
  product?: Product;
};

// LEDGER
export type LedgerTransactionType = 'purchase' | 'assignment';

export type LedgerTransaction = {
  id: string;
  date: string;
  type: LedgerTransactionType;
  product_id: string;
  product_name: string;
  quantity: number; // positive for purchases, negative for assignments
  related_to?: string; // vendor name or client name
  related_id?: string; // vendor_id or client_id
  amount?: number;
  batch_number?: string;
  notes?: string;
  running_balance?: number; // calculated
};

export type LedgerTransactionEnhanced = LedgerTransaction & {
  cost_per_unit?: number;
  selling_price_per_unit?: number;
  profit?: number;
};

export type LedgerFilters = {
  product_id?: string;
  vendor_id?: string;
  client_id?: string;
  transaction_type?: 'purchase' | 'assignment' | 'all';
  date_from?: string;
  date_to?: string;
};

export type LedgerResponse = {
  transactions: LedgerTransactionEnhanced[];
  total_count: number;
  summary?: {
    total_profit: number;
    current_balance: number;
    total_in: number;
    total_out: number;
  };
};

// FORM TYPES
export type VendorFormData = {
  name: string;
  company_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  default_credit_days?: number;
  notes?: string;
  is_active?: boolean;
};

export type ProductFormData = {
  name: string;
  brand?: string;
  form?: string;
  dosage?: string;
  default_selling_price?: number;
  unit_of_measure?: string;
  low_stock_threshold?: number;
  description?: string;
  instructions?: string;
  is_active?: boolean;
};

export type PurchaseFormData = {
  vendor_id: string;
  product_id: string;
  quantity: number;
  cost_per_unit: number;
  batch_number?: string;
  expiry_date?: string;
  purchase_date?: string;
  invoice_number?: string;
  notes?: string;
  payment_status?: 'pending' | 'partial' | 'paid';
  amount_paid?: number;
  payment_due_date?: string;
};

export type VendorPaymentFormData = {
  vendor_id: string;
  purchase_id?: string;
  amount: number;
  payment_method?: string;
  payment_date?: string;
  transaction_id?: string;
  notes?: string;
};

export type ClientSupplementFormData = {
  product_id: string;
  quantity: number;
  selling_price_per_unit: number;
  dosage_instructions?: string;
  duration_days?: number;
  follow_up_date?: string;
  notes?: string;

  // Payment options
  appointment_id?: string;
  payment_option?: 'new' | 'existing' | 'none';
  payment_due_date?: string;

  // Reminder options
  create_refill_reminder?: boolean;

  // Legacy support (deprecated)
  create_payment?: boolean;
  create_reminder?: boolean;
};

// STOCK STATUS
export type StockStatus = 'out' | 'low' | 'ok' | 'good';

export function getStockStatus(currentStock: number, threshold: number): StockStatus {
  if (currentStock === 0) return 'out';
  if (currentStock <= threshold) return 'low';
  if (currentStock <= threshold * 2) return 'ok';
  return 'good';
}

export function getStockStatusColor(status: StockStatus): string {
  switch (status) {
    case 'out':
      return 'bg-red-100 text-red-800';
    case 'low':
      return 'bg-orange-100 text-orange-800';
    case 'ok':
      return 'bg-yellow-100 text-yellow-800';
    case 'good':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// PAYMENT STATUS
export function getPaymentStatusColor(status: 'pending' | 'partial' | 'paid'): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// =============================================
// Enhanced Payment Types with Line Items
// =============================================

export type PaymentInputMode = 'quick' | 'detailed';

export type PaymentWithLineItems = {
  id: string;
  user_id: string;
  client_id: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'expired';
  due_at: string;
  paid_at?: string;
  payment_method?: string;
  transaction_id?: string;
  notes?: string;
  reminder_sent_at?: string;
  last_reminder_sent_at?: string;
  confirmation_sent_at?: string;
  appointment_id?: string;
  // Detailed mode fields
  input_mode?: PaymentInputMode;
  subtotal?: number;
  overall_discount_type?: DiscountType | null;
  overall_discount_value?: number;
  overall_discount_amount?: number;
  overall_discount_reason?: string | null;
  tax_enabled?: boolean;
  tax_rate?: number;
  tax_amount?: number;
  created_at: string;
  updated_at: string;
  line_items?: PaymentLineItemWithDetails[];
};

export type EnhancedPaymentLineItemWithDetails = PaymentLineItem & {
  product?: {
    name: string;
    brand?: string;
    form?: string;
    dosage?: string;
  };
};
